import React, { useEffect, useState } from 'react';
import { DndContext, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { customCollisionDetectionAlgorithm } from '../utils/DndropUtils';
import BaseDashboardPanel from './Dashboard/BaseDashboardPanel';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { Backdrop, Box, Button, Paper, Typography } from '@mui/material';
import DashboardWidgetApiService from '../api/DashboardWidgetApiService';
import {
    getBaseDashboardComponents,
    getDefaultDashboardWidgetJsonSettings,
    getDashboardWidgetComponent,
    getDefaultDashboardWidgets,
} from '../utilities/DashboardUtilities';
import DashboardWidgetModal from './Dashboard/DashboardWidgetModal';
import ConfirmDeleteDialog from './Modal/ConfirmDelete';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from './Common/SnackbarProvider';

const dashboardWidgetApiService = new DashboardWidgetApiService();
const Dashboard = () => {
    const snackbar = useSnackbar();
    const { t } = useTranslation();
    const [locked, setLocked] = useState(true);
    const [dashboardWidgets, setDashboardWidgets] = useState(getBaseDashboardComponents());
    const [updatedDashboardWidgets, setUpdatedDashboardWidgets] = useState(getBaseDashboardComponents());
    const [activeId, setActiveId] = useState(null);
    const [overId, setOverId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [showDeleteWidget, setShowDeleteWidget] = useState(false);
    const [selectedDeleteWidget, setSelectedDeleteWidget] = useState(null);
    const [selectedEditWidget, setSelectedEditWidget] = useState(null);

    const positions = [
        { top: 0, left: 0 },
        { top: 0, left: '50%' },
        { top: '50%', left: 0 },
        { top: '50%', left: '50%' },
    ];

    useEffect(() => {
        void loadDashboardWidgets();
    }, []);

    async function loadDashboardWidgets() {
        setLoading(true);
        let response = await dashboardWidgetApiService.getDashboardWidgets();

        let dashboardWidgetsFromApi = response.data;

        if (!dashboardWidgetsFromApi || dashboardWidgetsFromApi.length === 0) {
            dashboardWidgetsFromApi = getDefaultDashboardWidgets();
        }

        let tempDashboardWidgets = getBaseDashboardComponents();

        dashboardWidgetsFromApi.forEach((dashboardWidget) => {
            dashboardWidget.userCreated = true;

            if (dashboardWidget.position !== null) {
                tempDashboardWidgets[dashboardWidget.position] = {
                    ...dashboardWidget,
                    component: dashboardWidget.component,
                    jsonSettings: dashboardWidget.jsonSettings || getDefaultDashboardWidgetJsonSettings(dashboardWidget.component)
                }
            }
        });

        setDashboardWidgets(tempDashboardWidgets);

        // We need a deep copy here so the updated and DB widgets don't reference the same Objects
        setUpdatedDashboardWidgets(tempDashboardWidgets.map(widget => ({ ...widget })));

        setLoading(false);
    }

    function handleDragEnd(event) {
        const { active, over } = event;

        if (!over) {
            return;
        }

        if (active.id !== over?.id) {
            let newDashboardWidgets = [...updatedDashboardWidgets];
            let activeWidget = newDashboardWidgets.find((widget) => widget.id === active.id);
            let overWidget = newDashboardWidgets.find((widget) => widget.id === over?.id);

            let curOverWidgetPosition = overWidget.position;
            overWidget.position = activeWidget.position;
            activeWidget.position = curOverWidgetPosition;

            setUpdatedDashboardWidgets(newDashboardWidgets);
        }

        setActiveId(null);
        setOverId(null);
    }

    function isDirty() {
        return JSON.stringify(updatedDashboardWidgets) !== JSON.stringify(dashboardWidgets);
    }

    async function handleSettingsChange(widgetId, newJsonSettings) {
        let tempDashboardWidgets = [...updatedDashboardWidgets];
        const widgetIndex = tempDashboardWidgets.findIndex(w => w.id === widgetId);
        if (widgetIndex !== -1) {
            tempDashboardWidgets[widgetIndex] = {
                ...tempDashboardWidgets[widgetIndex],
                jsonSettings: newJsonSettings
            };

            if (locked) {
                await postWidgets(tempDashboardWidgets);
            }

            setUpdatedDashboardWidgets(tempDashboardWidgets);
        }
    }

    async function onSave() {
        if (!isDirty()) {
            setLocked(true);
            return;
        }

        setLoading(true);

        await postWidgets(updatedDashboardWidgets);

        snackbar.success(t('dashboard.toast.saved'));

        setLocked(true);
        setLoading(false);

        await loadDashboardWidgets();
    }

    async function postWidgets(widgetsToSave) {
        let postedDashboardWidgets = widgetsToSave.map(widget => {
            let widgetId = 0;

            if (widget.component === '' || !widget.component) {
                return null;
            }

            if (typeof widget.id !== 'string') {
                widgetId = widget.id;
            }

            return {
                ...widget,
                id: widgetId,
                component: widget.component,
                jsonSettings: widget.jsonSettings || getDefaultDashboardWidgetJsonSettings(widget.component)
            };
        }).filter(widget => widget !== null);

        await dashboardWidgetApiService.postDashboardWidgets(postedDashboardWidgets);
    }

    function onCancel() {
        // Reset back to the original dashboard widget layout
        setUpdatedDashboardWidgets(dashboardWidgets.map(widget => ({ ...widget })));

        setLocked(true);
    }

    async function toggledLocked() {
        if (!locked) {
            await onSave();

            setActiveId(null);
            setOverId(null);
        }

        setLocked(!locked);
    }

    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 10,
            },
        })
    );

    function saveWidget(widget) {
        // Save the new widget we're adding to the dashboard
        let tempDashboardWidgets = [...updatedDashboardWidgets];
        tempDashboardWidgets[widget.position] = widget;
        setUpdatedDashboardWidgets(tempDashboardWidgets);

        if (locked) {
            void postWidgets(tempDashboardWidgets);
        }

        setDrawerOpen(false);
        setSelectedEditWidget(null);
        setSelectedPosition(null);
    }

    function onDeleteWidget(value) {
        if (value) {
            let tempUpdatedDashboardWidgets = [...updatedDashboardWidgets];

            // Now update the position to be a generic box
            tempUpdatedDashboardWidgets[selectedDeleteWidget.position] =
                { id: `box${selectedDeleteWidget.position}`, position: selectedDeleteWidget.position }

            setUpdatedDashboardWidgets(tempUpdatedDashboardWidgets);
        }

        setShowDeleteWidget(false);
        setSelectedDeleteWidget(null);
    }

    return (
        <>
            <Backdrop
                open={loading}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    color: '#fff',
                }}
            >
            </Backdrop>
            <div style={{
                width: '99%',
                height: '5vh',
                position: 'relative',
                justifyContent: 'space-between',
                display: 'flex',
            }}>
                <Typography variant='h3'>{t('dashboard.title')}</Typography>
                <button
                    onClick={toggledLocked}
                    style={{
                        marginTop: '5px',
                        marginBottom: '5px',
                        width: '120px',
                    }}
                >
                    {locked ? t('dashboard.locked') : t('dashboard.unlocked')} {locked ? <LockIcon/> : <LockOpenIcon/>}
                </button>
            </div>
            <DndContext
                collisionDetection={customCollisionDetectionAlgorithm}
                onDragEnd={handleDragEnd}
                onDragStart={(event) => setActiveId(event.active.id)}
                onDragOver={(event) => setOverId(event.over?.id ?? null)}
                onDragCancel={() => {
                    setActiveId(null);
                    setOverId(null);
                }}
                sensors={sensors}
            >
                <div style={{
                    width: '100%',
                    height: '90vh',
                    position: 'relative',
                }}>
                    {updatedDashboardWidgets.map((box) => {
                        if (box.position !== null) {
                            return (
                                <BaseDashboardPanel
                                    key={box.id}
                                    id={box.id}
                                    position={positions[box.position]}
                                    color={box.color}
                                    locked={locked}
                                    activeId={activeId}
                                    overId={overId}
                                    name={box.name}
                                    onClick={() => {
                                        if (!box.component) {
                                            setSelectedPosition(box.position);
                                            setSelectedEditWidget(null);
                                            setDrawerOpen(true);
                                        }
                                    }}
                                    onRemoveClick={() => {
                                        setShowDeleteWidget(true);
                                        setSelectedDeleteWidget(box);
                                    }}
                                    onEditClick={() => {
                                        setSelectedPosition(box.position);
                                        setSelectedEditWidget(box);
                                        setDrawerOpen(true);
                                    }}
                                >
                                    {box.component
                                        ? getDashboardWidgetComponent(
                                            box.component,
                                            box.jsonSettings,
                                            (newJsonSettings) => handleSettingsChange(box.id, newJsonSettings)
                                        )
                                        : null}
                                </BaseDashboardPanel>
                            );
                        }

                        return null;
                    })}
                </div>
            </DndContext>
            {!locked && (
                <Paper
                    elevation={6}
                    sx={{
                        position: 'fixed',
                        bottom: 20,
                        right: 20,
                        zIndex: 1300,
                        p: 1.5,
                        backgroundColor: '#fff',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        boxShadow: 6,
                        borderRadius: 2,
                    }}
                >
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 1.5,
                        }}
                    >
                        <Button
                            onClick={onCancel}
                            color='secondary'
                            variant='outlined'
                            size='small'
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            onClick={onSave}
                            color='primary'
                            variant='contained'
                            size='small'
                        >
                            {t('common.save')}
                        </Button>
                    </Box>
                </Paper>
            )}
            <DashboardWidgetModal
                onClose={() => {
                    setDrawerOpen(false);
                    setSelectedEditWidget(null);
                    setSelectedPosition(null);
                }}
                onSave={(widget) => saveWidget(widget)}
                open={drawerOpen}
                position={selectedPosition}
                widgetToEdit={selectedEditWidget}
            />
            <ConfirmDeleteDialog
                onClose={(value) => onDeleteWidget(value)}
                open={showDeleteWidget}
                content={t('dashboard.confirmRemoveContent', { name: selectedDeleteWidget?.name ?? '' })}
                title={t('dashboard.confirmRemoveTitle')}
            />
        </>
    );
};

export default Dashboard;
